/**
 * Customer Repository Implementation
 * Implements the CustomerRepository interface
 * Coordinates between API calls and mappers
 */

import { CustomerRepository } from "../../domain/repositories/CustomerRepository";
import {
  CustomerEntity,
  CustomerSearchResultEntity,
  CustomerOperationResult,
  CustomerDetailResult,
  CustomerGroupsResult,
  CustomerGroupEntity,
} from "../../domain/entities/CustomerEntity";
import {
  createCustomer,
  updateCustomer,
  getCustomerById,
  searchCustomers,
  fetchCustomers,
} from "../api/customersApi";
import {
  fetchCustomerGroups,
  createCustomerGroup,
} from "../../../../shared/data/api/currenciesApi";
import { CustomerMapper } from "../mappers/CustomerMapper";
import { CustomerDataDTO } from "../models/CustomerDTOs";

export class CustomerRepositoryImpl implements CustomerRepository {
  /**
   * Create a new customer
   */
  async createCustomer(
    customerData: Partial<CustomerEntity>
  ): Promise<CustomerOperationResult> {
    try {
      const customerDataDTO = CustomerMapper.toCustomerDataDTO(customerData);
      const response = await createCustomer(customerDataDTO);
      const responseData = response.data as {
        customer_id?: number;
        customerid?: string;
        zoho_customerid?: string;
      };

      if (responseData?.customer_id != null) {
        const mappedEntity = CustomerMapper.toCustomerEntity({
          ...customerDataDTO,
          customerid: String(responseData.customer_id),
          zoho_customerid:
            responseData.zoho_customerid ?? customerData.zoho_customerid ?? "",
        } as CustomerDataDTO);

        return {
          success: response.success,
          message: response.message,
          data: mappedEntity,
        };
      }

      const responseCustomerData = (response.data as any)?.customer || response.data;
      if (!responseCustomerData) {
        return {
          success: false,
          message: "No customer data in API response",
          error: "Customer data is missing from API response",
        };
      }

      return {
        success: response.success,
        message: response.message,
        data: CustomerMapper.toCustomerEntity(responseCustomerData),
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to create customer",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Update an existing customer
   */
  async updateCustomer(
    customerId: string,
    customerData: Partial<CustomerEntity>
  ): Promise<CustomerOperationResult> {
    try {
      const customerDataDTO = CustomerMapper.toCustomerDataDTO(customerData);
      const response = await updateCustomer(customerId, customerDataDTO);
      const responseData = response.data as { customer_id?: number; customerid?: string };

      if (responseData?.customer_id != null) {
        const mappedEntity = CustomerMapper.toCustomerEntity({
          ...customerDataDTO,
          customerid: String(responseData.customer_id),
          zoho_customerid: customerData.zoho_customerid ?? "",
        } as CustomerDataDTO);

        return {
          success: response.success,
          message: response.message,
          data: mappedEntity,
        };
      }

      const responseCustomerData = (response.data as any)?.customer || response.data;
      if (!responseCustomerData) {
        return {
          success: false,
          message: "No customer data in API response",
          error: "Customer data is missing from API response",
        };
      }

      return {
        success: response.success,
        message: response.message,
        data: CustomerMapper.toCustomerEntity(responseCustomerData),
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to update customer",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Get a customer by ID
   */
  async getCustomerById(customerId: string): Promise<CustomerDetailResult> {
    try {
      const response = await getCustomerById(customerId);

      return {
        success: response.success,
        message: response.message,
        data: CustomerMapper.toCustomerEntity(response.data),
      };
    } catch (error: any) {
      throw new Error(error.message || "Failed to fetch customer");
    }
  }

  /**
   * Search customers with pagination
   */
  async searchCustomers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<CustomerSearchResultEntity> {
    try {
      const response = await searchCustomers(params);

      // Note: API returns CustomerDataDTO[], but we need to treat them as search items
      // Map each customer data to a search item entity (with customer_id field)
      return {
        success: response.success,
        data: response.data.map((customerDTO: any) =>
          CustomerMapper.toCustomerSearchItemEntity(customerDTO)
        ),
        pagination: response.pagination,
      };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        pagination: {
          total: 0,
          limit: params?.limit || 10,
          offset: 0,
          has_more: false,
        },
      };
    }
  }

  /**
   * Fetch customers using vector similarity search (invoice-specific)
   */
  async fetchCustomers(
    query: string,
    options?: {
      customerId?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<CustomerSearchResultEntity> {
    try {
      const response = await fetchCustomers(query, options);

      return {
        success: response.success,
        data: response.data.map((customerDTO) =>
          CustomerMapper.toCustomerSearchItemEntity(customerDTO)
        ),
        pagination: {
          total: response.data.length,
          limit: response.data.length,
          offset: 0,
          has_more: false,
        },
      };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        pagination: {
          total: 0,
          limit: 10,
          offset: 0,
          has_more: false,
        },
      };
    }
  }

  /**
   * Fetch customer groups
   */
  async fetchCustomerGroups(): Promise<CustomerGroupsResult> {
    try {
      const response = await fetchCustomerGroups();

      return {
        success: response.success,
        message: response.message,
        data: response.data.map((groupDTO: any) =>
          CustomerMapper.toCustomerGroupEntity(groupDTO)
        ),
      };
    } catch (error: any) {
      throw new Error(error.message || "Failed to fetch customer groups");
    }
  }

  /**
   * Create a new customer group
   */
  async createCustomerGroup(groupData: {
    group_name: string;
    group_description?: string;
  }): Promise<{
    success: boolean;
    message: string;
    data: CustomerGroupEntity;
  }> {
    try {
      const response = await createCustomerGroup(groupData);

      return {
        success: response.success,
        message: response.message,
        data: CustomerMapper.toCustomerGroupEntity(response.data as any),
      };
    } catch (error: any) {
      throw new Error(error.message || "Failed to create customer group");
    }
  }
}
